package com.mgacreative.hotel.feature.housekeeping.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotel.domain.repository.InventoryItem
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

enum class TaskStatus(val color: Color, val bgColor: Color, val label: String) {
    DIRTY(Color(0xFFDC2626), Color(0xFFFEE2E2), "Kirli (Temizlenecek)"),
    IN_PROGRESS(Color(0xFFD97706), Color(0xFFFEF3C7), "Temizlik Sürüyor"),
    CLEAN(Color(0xFF16A34A), Color(0xFFDCFCE7), "Temiz (Hazır)")
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TaskListScreen(
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {},
    viewModel: HousekeepingViewModel = koinViewModel()
) {
    val tasks by viewModel.tasks.collectAsState()
    val inventoryItems by viewModel.inventoryItems.collectAsState()
    val errorMessage by viewModel.errorMessage.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.loadTasks()
    }

    var selectedFilter by remember { mutableStateOf("ALL") } // ALL, DIRTY, IN_PROGRESS, CLEAN
    var searchQuery by remember { mutableStateOf("") }

    val dirtyCount = tasks.count { it.status == "PENDING" }
    val inProgressCount = tasks.count { it.status == "IN_PROGRESS" }
    val cleanCount = tasks.count { it.status == "DONE" }

    val filteredTasks = tasks.filter { task ->
        val matchesFilter = when (selectedFilter) {
            "DIRTY" -> task.status == "PENDING"
            "IN_PROGRESS" -> task.status == "IN_PROGRESS"
            "CLEAN" -> task.status == "DONE"
            else -> true
        }
        val matchesSearch = searchQuery.isBlank() || task.room_number.contains(searchQuery, ignoreCase = true)
        matchesFilter && matchesSearch
    }

    var showStatusDialogForTask by remember { mutableStateOf<HousekeepingTask?>(null) }
    var showSuppliesDialogForTask by remember { mutableStateOf<HousekeepingTask?>(null) }
    var showMinibarDialogForTask by remember { mutableStateOf<HousekeepingTask?>(null) }
    var showMaintenanceDialogForTask by remember { mutableStateOf<HousekeepingTask?>(null) }
    var showAddTaskDialog by remember { mutableStateOf(false) }

    val snackbarHostState = remember { SnackbarHostState() }

    LaunchedEffect(errorMessage) {
        if (errorMessage != null) {
            snackbarHostState.showSnackbar(errorMessage ?: "")
            viewModel.clearError()
        }
    }

    Scaffold(
        containerColor = Color(0xFFF8FAFC),
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            Surface(
                color = Color.White,
                shadowElevation = 1.dp
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        IconButton(
                            onClick = onNavigateBack,
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(
                                Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = l10n("Back"),
                                tint = Color(0xFF1E293B),
                                modifier = Modifier.size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(12.dp))
                        Column {
                            Text(
                                text = "Kat Hizmetleri & Oda Temizliği",
                                fontWeight = FontWeight.Bold,
                                fontSize = 18.sp,
                                color = Color(0xFF0F172A)
                            )
                            Text(
                                text = "Oda Durumları, Temizlik ve Minibar Takibi",
                                fontSize = 12.sp,
                                color = Color(0xFF64748B)
                            )
                        }
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        IconButton(
                            onClick = { viewModel.loadTasks() },
                            modifier = Modifier
                                .size(36.dp)
                                .background(Color(0xFFF1F5F9), CircleShape)
                        ) {
                            Icon(Icons.Default.Refresh, contentDescription = "Yenile", tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                        }

                        Button(
                            onClick = { showAddTaskDialog = true },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                            shape = RoundedCornerShape(10.dp),
                            contentPadding = PaddingValues(horizontal = 14.dp, vertical = 8.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = null, tint = Color.White, modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Görev Ata", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
                        }
                    }
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            HousekeepingBottomNavBar(selectedTab = 0, onNavigate = onNavigate)
        }
    ) { paddingValues ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF8FAFC)),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // KPI Summary Cards
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    // Toplam Oda
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text("Toplam Oda", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("${tasks.size}", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFF0F172A))
                        }
                    }

                    // Kirli Odalar
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text("Kirli Odalar", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("$dirtyCount", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFFDC2626))
                        }
                    }

                    // Temizlik Sürüyor
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text("Temizlikte", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("$inProgressCount", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFFD97706))
                        }
                    }

                    // Temiz & Hazır
                    Surface(
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                        shadowElevation = 1.dp
                    ) {
                        Column(modifier = Modifier.padding(14.dp)) {
                            Text("Temiz & Hazır", color = Color(0xFF64748B), fontSize = 12.sp, fontWeight = FontWeight.Medium)
                            Spacer(modifier = Modifier.height(4.dp))
                            Text("$cleanCount", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = Color(0xFF16A34A))
                        }
                    }
                }
            }

            // Search & Filter Row
            item {
                Surface(
                    shape = RoundedCornerShape(16.dp),
                    color = Color.White,
                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(12.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        OutlinedTextField(
                            value = searchQuery,
                            onValueChange = { searchQuery = it },
                            placeholder = { Text("Oda no ile ara (Örn: 104)...", fontSize = 13.sp, color = Color(0xFF94A3B8)) },
                            leadingIcon = {
                                Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(18.dp))
                            },
                            trailingIcon = {
                                if (searchQuery.isNotEmpty()) {
                                    IconButton(onClick = { searchQuery = "" }) {
                                        Icon(Icons.Default.Clear, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(16.dp))
                                    }
                                }
                            },
                            singleLine = true,
                            shape = RoundedCornerShape(10.dp),
                            colors = OutlinedTextFieldDefaults.colors(
                                focusedBorderColor = Color(0xFF2563EB),
                                unfocusedBorderColor = Color(0xFFE2E8F0),
                                focusedContainerColor = Color.White,
                                unfocusedContainerColor = Color(0xFFF8FAFC)
                            ),
                            modifier = Modifier
                                .weight(1f)
                                .height(48.dp)
                        )

                        Spacer(modifier = Modifier.width(12.dp))

                        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            HkFilterChip(
                                label = "Tümü",
                                count = tasks.size,
                                isSelected = selectedFilter == "ALL",
                                onClick = { selectedFilter = "ALL" }
                            )
                            HkFilterChip(
                                label = "Kirli",
                                count = dirtyCount,
                                isSelected = selectedFilter == "DIRTY",
                                selectedColor = Color(0xFFDC2626),
                                onClick = { selectedFilter = "DIRTY" }
                            )
                            HkFilterChip(
                                label = "Temizlikte",
                                count = inProgressCount,
                                isSelected = selectedFilter == "IN_PROGRESS",
                                selectedColor = Color(0xFFD97706),
                                onClick = { selectedFilter = "IN_PROGRESS" }
                            )
                            HkFilterChip(
                                label = "Temiz",
                                count = cleanCount,
                                isSelected = selectedFilter == "CLEAN",
                                selectedColor = Color(0xFF16A34A),
                                onClick = { selectedFilter = "CLEAN" }
                            )
                        }
                    }
                }
            }

            // Room Cards Responsive Grid (Up to 4 columns on wide screens)
            if (filteredTasks.isEmpty()) {
                item {
                    Surface(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 32.dp),
                        shape = RoundedCornerShape(16.dp),
                        color = Color.White,
                        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
                    ) {
                        Column(
                            modifier = Modifier.padding(32.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Icon(Icons.Default.Info, contentDescription = null, tint = Color(0xFF94A3B8), modifier = Modifier.size(40.dp))
                            Spacer(modifier = Modifier.height(12.dp))
                            Text("Seçilen filtreye uygun oda bulunamadı.", color = Color(0xFF64748B), fontSize = 14.sp)
                        }
                    }
                }
            } else {
                item {
                    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                        val columns = when {
                            maxWidth >= 1200.dp -> 4
                            maxWidth >= 880.dp -> 3
                            maxWidth >= 580.dp -> 2
                            else -> 1
                        }

                        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                            filteredTasks.chunked(columns).forEach { rowTasks ->
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                                ) {
                                    rowTasks.forEach { task ->
                                        Box(modifier = Modifier.weight(1f)) {
                                            ModernHousekeepingCard(
                                                task = task,
                                                onCardClick = { showStatusDialogForTask = task },
                                                onStart = { viewModel.startTask(task.id) },
                                                onFinish = { showSuppliesDialogForTask = task },
                                                onMinibar = { showMinibarDialogForTask = task },
                                                onMaintenance = { showMaintenanceDialogForTask = task },
                                                onMarkDirty = { viewModel.markAsDirty(task.id) }
                                            )
                                        }
                                    }
                                    val emptySlots = columns - rowTasks.size
                                    if (emptySlots > 0) {
                                        repeat(emptySlots) {
                                            Spacer(modifier = Modifier.weight(1f))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            item {
                Spacer(modifier = Modifier.height(40.dp))
            }
        }
    }

    // Status Action Quick Dialog
    if (showStatusDialogForTask != null) {
        val task = showStatusDialogForTask!!
        AlertDialog(
            onDismissRequest = { showStatusDialogForTask = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Text(
                    text = "Oda ${task.room_number} - İşlem Seçin",
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    color = Color(0xFF0F172A)
                )
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Bu oda için gerçekleştirmek istediğiniz işlemi seçiniz:", fontSize = 13.sp, color = Color(0xFF64748B))

                    // Action Buttons
                    OutlinedButton(
                        onClick = {
                            viewModel.startTask(task.id)
                            showStatusDialogForTask = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Default.PlayArrow, contentDescription = null, tint = Color(0xFFD97706))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Temizliği Başlat (İşlemde)", color = Color(0xFFD97706), fontWeight = FontWeight.Bold)
                    }

                    Button(
                        onClick = {
                            showSuppliesDialogForTask = task
                            showStatusDialogForTask = null
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Default.Check, contentDescription = null, tint = Color.White)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Temizlendi (Malzeme Düşerek)", color = Color.White, fontWeight = FontWeight.Bold)
                    }

                    OutlinedButton(
                        onClick = {
                            showMinibarDialogForTask = task
                            showStatusDialogForTask = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Default.ShoppingCart, contentDescription = null, tint = Color(0xFF2563EB))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Minibar Harcaması Ekle", color = Color(0xFF2563EB), fontWeight = FontWeight.Bold)
                    }

                    OutlinedButton(
                        onClick = {
                            showMaintenanceDialogForTask = task
                            showStatusDialogForTask = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Default.Build, contentDescription = null, tint = Color(0xFFDC2626))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Arıza & Bakım Bildir", color = Color(0xFFDC2626), fontWeight = FontWeight.Bold)
                    }

                    OutlinedButton(
                        onClick = {
                            viewModel.markAsDirty(task.id)
                            showStatusDialogForTask = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Icon(Icons.Default.Refresh, contentDescription = null, tint = Color(0xFF64748B))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Kirli Olarak İşaretle", color = Color(0xFF64748B))
                    }
                }
            },
            confirmButton = {},
            dismissButton = {
                TextButton(onClick = { showStatusDialogForTask = null }) {
                    Text("Kapat", color = Color(0xFF64748B))
                }
            }
        )
    }

    // Supplies & Finish Cleaning Dialog
    if (showSuppliesDialogForTask != null) {
        val task = showSuppliesDialogForTask!!
        val suppliesMap = remember { mutableStateMapOf<String, Int>() }

        AlertDialog(
            onDismissRequest = { showSuppliesDialogForTask = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Text("Oda ${task.room_number} - Kullanılan Malzemeler", fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A))
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth().heightIn(max = 350.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text("Temizlik sırasında kullanılan malzemeleri seçin (Envanter stokundan otomatik düşülecektir):", fontSize = 12.sp, color = Color(0xFF64748B))

                    if (inventoryItems.isEmpty()) {
                        Text("Stokta kayıtlı temizlik malzemesi bulunamadı.", fontSize = 13.sp, color = Color(0xFFDC2626), modifier = Modifier.padding(vertical = 12.dp))
                    } else {
                        LazyColumn(
                            verticalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            items(inventoryItems) { item ->
                                val count = suppliesMap[item.id] ?: 0
                                Surface(
                                    shape = RoundedCornerShape(10.dp),
                                    color = Color(0xFFF8FAFC),
                                    border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(10.dp),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text(item.name, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF0F172A))
                                            Text("Mevcut Stok: ${item.quantity} ${item.unit}", fontSize = 11.sp, color = Color(0xFF64748B))
                                        }
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            IconButton(
                                                onClick = { if (count > 0) suppliesMap[item.id] = count - 1 },
                                                modifier = Modifier.size(32.dp).background(Color.White, CircleShape)
                                            ) {
                                                Text("-", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color(0xFFDC2626))
                                            }
                                            Text("$count", fontWeight = FontWeight.Bold, fontSize = 14.sp, modifier = Modifier.padding(horizontal = 8.dp))
                                            IconButton(
                                                onClick = { if (count < item.quantity) suppliesMap[item.id] = count + 1 },
                                                modifier = Modifier.size(32.dp).background(Color.White, CircleShape)
                                            ) {
                                                Text("+", fontSize = 18.sp, fontWeight = FontWeight.Bold, color = Color(0xFF16A34A))
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        viewModel.finishTaskWithSupplies(task.id, suppliesMap.toMap())
                        showSuppliesDialogForTask = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text("Temizliği Tamamla & Stok Düş", fontWeight = FontWeight.Bold, color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { showSuppliesDialogForTask = null }) {
                    Text("İptal", color = Color(0xFF64748B))
                }
            }
        )
    }

    // Minibar Dialog
    if (showMinibarDialogForTask != null) {
        val task = showMinibarDialogForTask!!
        var waterCount by remember { mutableStateOf(0) }
        var sodaCount by remember { mutableStateOf(0) }
        var colaCount by remember { mutableStateOf(0) }
        var snackCount by remember { mutableStateOf(0) }

        val totalAmount = (waterCount * 25.0) + (sodaCount * 35.0) + (colaCount * 50.0) + (snackCount * 60.0)

        AlertDialog(
            onDismissRequest = { showMinibarDialogForTask = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Text("Oda ${task.room_number} - Minibar Tüketimi", fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A))
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Tüketilen minibar ürünlerini seçin. Tutar oda folyosuna işlenecektir.", fontSize = 12.sp, color = Color(0xFF64748B))

                    MinibarCounterRow("Su (0.5L)", "₺ 25.00", waterCount, onDec = { if (waterCount > 0) waterCount-- }, onInc = { waterCount++ })
                    MinibarCounterRow("Maden Suyu", "₺ 35.00", sodaCount, onDec = { if (sodaCount > 0) sodaCount-- }, onInc = { sodaCount++ })
                    MinibarCounterRow("Kola / Meşrubat", "₺ 50.00", colaCount, onDec = { if (colaCount > 0) colaCount-- }, onInc = { colaCount++ })
                    MinibarCounterRow("Cips & Kuruyemiş", "₺ 60.00", snackCount, onDec = { if (snackCount > 0) snackCount-- }, onInc = { snackCount++ })

                    Spacer(modifier = Modifier.height(4.dp))
                    Surface(
                        shape = RoundedCornerShape(10.dp),
                        color = Color(0xFFEFF6FF),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Row(
                            modifier = Modifier.padding(12.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Toplam Minibar Tutarı:", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF1E3A8A))
                            Text("₺ ${totalAmount}", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = Color(0xFF2563EB))
                        }
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        val items = mutableMapOf<String, Int>()
                        if (waterCount > 0) items["Su"] = waterCount
                        if (sodaCount > 0) items["Maden Suyu"] = sodaCount
                        if (colaCount > 0) items["Kola"] = colaCount
                        if (snackCount > 0) items["Cips & Kuruyemiş"] = snackCount

                        if (totalAmount > 0) {
                            viewModel.addMinibarCharge(task.id, items, totalAmount)
                        }
                        showMinibarDialogForTask = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text("Oda Hesabına Yaz", fontWeight = FontWeight.Bold, color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { showMinibarDialogForTask = null }) {
                    Text("İptal", color = Color(0xFF64748B))
                }
            }
        )
    }

    // Direct Report Maintenance Dialog
    if (showMaintenanceDialogForTask != null) {
        val task = showMaintenanceDialogForTask!!
        var issueDescription by remember { mutableStateOf("") }

        AlertDialog(
            onDismissRequest = { showMaintenanceDialogForTask = null },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = {
                Text("Oda ${task.room_number} - Arıza Bildir", fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A))
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Bu oda için tespit edilen arıza detayını yazın. Teknik servise iletilecek ve oda bakım durumuna alınacaktır.", fontSize = 12.sp, color = Color(0xFF64748B))
                    OutlinedTextField(
                        value = issueDescription,
                        onValueChange = { issueDescription = it },
                        label = { Text("Arıza Açıklaması", fontSize = 12.sp) },
                        placeholder = { Text("Örn: Klima soğutmuyor veya banyo musluğu damlatıyor...", color = Color(0xFF94A3B8)) },
                        shape = RoundedCornerShape(12.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = Color(0xFFDC2626),
                            unfocusedBorderColor = Color(0xFFCBD5E1),
                            focusedContainerColor = Color.White,
                            unfocusedContainerColor = Color(0xFFF8FAFC)
                        ),
                        modifier = Modifier.fillMaxWidth().height(100.dp)
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (issueDescription.isNotBlank()) {
                            viewModel.reportMaintenance(task.id, issueDescription.trim())
                        }
                        showMaintenanceDialogForTask = null
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFDC2626)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text("Arızayı Kaydet", fontWeight = FontWeight.Bold, color = Color.White)
                }
            },
            dismissButton = {
                TextButton(onClick = { showMaintenanceDialogForTask = null }) {
                    Text("İptal", color = Color(0xFF64748B))
                }
            }
        )
    }

    // Add Task Dialog
    if (showAddTaskDialog) {
        AlertDialog(
            onDismissRequest = { showAddTaskDialog = false },
            containerColor = Color.White,
            shape = RoundedCornerShape(20.dp),
            title = { Text("Yeni Temizlik Görevi Planla", fontWeight = FontWeight.Bold, fontSize = 17.sp, color = Color(0xFF0F172A)) },
            text = {
                Text("Kat görevlisi atamaları ve otomatik temizlik görevleri sistem tarafından oda check-in / check-out durumlarına göre senkronize edilmektedir.", fontSize = 13.sp, color = Color(0xFF64748B))
            },
            confirmButton = {
                Button(
                    onClick = { showAddTaskDialog = false },
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2563EB)),
                    shape = RoundedCornerShape(10.dp)
                ) {
                    Text("Anladım", color = Color.White)
                }
            }
        )
    }
}

@Composable
fun MinibarCounterRow(name: String, price: String, count: Int, onDec: () -> Unit, onInc: () -> Unit) {
    Surface(
        shape = RoundedCornerShape(10.dp),
        color = Color(0xFFF8FAFC),
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column {
                Text(name, fontWeight = FontWeight.Bold, fontSize = 13.sp, color = Color(0xFF0F172A))
                Text(price, fontSize = 11.sp, color = Color(0xFF64748B))
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                IconButton(onClick = onDec, modifier = Modifier.size(28.dp).background(Color.White, CircleShape)) {
                    Text("-", fontSize = 16.sp, fontWeight = FontWeight.Bold, color = Color(0xFFDC2626))
                }
                Text("$count", fontWeight = FontWeight.Bold, fontSize = 14.sp, modifier = Modifier.padding(horizontal = 8.dp))
                IconButton(onClick = onInc, modifier = Modifier.size(28.dp).background(Color.White, CircleShape)) {
                    Text("+", fontSize = 16.sp, fontWeight = FontWeight.Bold, color = Color(0xFF16A34A))
                }
            }
        }
    }
}

@Composable
fun HkFilterChip(
    label: String,
    count: Int,
    isSelected: Boolean,
    selectedColor: Color = Color(0xFF2563EB),
    onClick: () -> Unit
) {
    Surface(
        shape = RoundedCornerShape(8.dp),
        color = if (isSelected) selectedColor.copy(alpha = 0.12f) else Color(0xFFF1F5F9),
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            if (isSelected) selectedColor else Color.Transparent
        ),
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Text(
                text = label,
                fontSize = 12.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) selectedColor else Color(0xFF475569)
            )
            Box(
                modifier = Modifier
                    .clip(CircleShape)
                    .background(if (isSelected) selectedColor else Color(0xFFCBD5E1))
                    .padding(horizontal = 5.dp, vertical = 1.dp)
            ) {
                Text(
                    text = count.toString(),
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                    color = if (isSelected) Color.White else Color(0xFF334155)
                )
            }
        }
    }
}

@Composable
fun ModernHousekeepingCard(
    task: HousekeepingTask,
    onCardClick: () -> Unit,
    onStart: () -> Unit,
    onFinish: () -> Unit,
    onMinibar: () -> Unit,
    onMaintenance: () -> Unit,
    onMarkDirty: () -> Unit
) {
    val uiStatus = when (task.status) {
        "PENDING" -> TaskStatus.DIRTY
        "IN_PROGRESS" -> TaskStatus.IN_PROGRESS
        else -> TaskStatus.CLEAN
    }

    Surface(
        shape = RoundedCornerShape(14.dp),
        color = Color.White,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0)),
        shadowElevation = 1.dp,
        modifier = Modifier.fillMaxWidth().clickable(onClick = onCardClick)
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            // Status indicator bar on left
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(4.dp)
                    .background(uiStatus.color)
                    .align(Alignment.CenterStart)
            )

            Column(modifier = Modifier.padding(12.dp)) {
                // Header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.weight(1f)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(32.dp)
                                .clip(RoundedCornerShape(8.dp))
                                .background(Color(0xFFEFF6FF)),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(Icons.Default.Home, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(18.dp))
                        }
                        Spacer(modifier = Modifier.width(8.dp))
                        Column {
                            Text("Oda ${task.room_number}", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF0F172A))
                            Text(task.assigned_to, fontSize = 11.sp, color = Color(0xFF64748B), maxLines = 1)
                        }
                    }

                    // Status Badge
                    Surface(
                        shape = RoundedCornerShape(6.dp),
                        color = uiStatus.bgColor,
                        border = androidx.compose.foundation.BorderStroke(1.dp, uiStatus.color.copy(alpha = 0.3f))
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            Box(modifier = Modifier.size(5.dp).clip(CircleShape).background(uiStatus.color))
                            Text(uiStatus.label, color = uiStatus.color, fontSize = 10.sp, fontWeight = FontWeight.Bold)
                        }
                    }
                }

                Spacer(modifier = Modifier.height(10.dp))

                // Action Buttons Row
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (task.status == "PENDING") {
                        Button(
                            onClick = onStart,
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFD97706)),
                            shape = RoundedCornerShape(6.dp),
                            contentPadding = PaddingValues(horizontal = 6.dp, vertical = 4.dp),
                            modifier = Modifier.weight(1f).height(32.dp)
                        ) {
                            Icon(Icons.Default.PlayArrow, contentDescription = null, tint = Color.White, modifier = Modifier.size(14.dp))
                            Spacer(modifier = Modifier.width(2.dp))
                            Text("Başlat", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        }
                    } else if (task.status == "IN_PROGRESS") {
                        Button(
                            onClick = onFinish,
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF16A34A)),
                            shape = RoundedCornerShape(6.dp),
                            contentPadding = PaddingValues(horizontal = 6.dp, vertical = 4.dp),
                            modifier = Modifier.weight(1f).height(32.dp)
                        ) {
                            Icon(Icons.Default.Check, contentDescription = null, tint = Color.White, modifier = Modifier.size(14.dp))
                            Spacer(modifier = Modifier.width(2.dp))
                            Text("Temizlendi", fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        }
                    } else {
                        OutlinedButton(
                            onClick = onMarkDirty,
                            shape = RoundedCornerShape(6.dp),
                            contentPadding = PaddingValues(horizontal = 6.dp, vertical = 4.dp),
                            modifier = Modifier.weight(1f).height(32.dp)
                        ) {
                            Icon(Icons.Default.Refresh, contentDescription = null, tint = Color(0xFFDC2626), modifier = Modifier.size(14.dp))
                            Spacer(modifier = Modifier.width(2.dp))
                            Text("Kirlet", fontSize = 11.sp, color = Color(0xFFDC2626))
                        }
                    }

                    // Minibar Button
                    OutlinedButton(
                        onClick = onMinibar,
                        shape = RoundedCornerShape(6.dp),
                        contentPadding = PaddingValues(horizontal = 6.dp, vertical = 4.dp),
                        modifier = Modifier.height(32.dp)
                    ) {
                        Icon(Icons.Default.ShoppingCart, contentDescription = null, tint = Color(0xFF2563EB), modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(2.dp))
                        Text("Minibar", fontSize = 11.sp, color = Color(0xFF2563EB))
                    }

                    // Maintenance Button
                    OutlinedButton(
                        onClick = onMaintenance,
                        shape = RoundedCornerShape(6.dp),
                        contentPadding = PaddingValues(4.dp),
                        modifier = Modifier.size(32.dp)
                    ) {
                        Icon(Icons.Default.Build, contentDescription = null, tint = Color(0xFF64748B), modifier = Modifier.size(14.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun HousekeepingBottomNavBar(
    selectedTab: Int,
    onNavigate: (String) -> Unit = {}
) {
    Surface(
        color = Color.White,
        shadowElevation = 8.dp,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0xFFE2E8F0))
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 8.dp, horizontal = 24.dp),
            horizontalArrangement = Arrangement.SpaceAround,
            verticalAlignment = Alignment.CenterVertically
        ) {
            HousekeepingNavItem(
                title = "Kat Hizmetleri",
                icon = Icons.Default.Home,
                isSelected = selectedTab == 0,
                onClick = { onNavigate("HousekeepingScreen") }
            )

            HousekeepingNavItem(
                title = "Bakım & Onarım",
                icon = Icons.Default.Build,
                isSelected = selectedTab == 1,
                onClick = { onNavigate("MaintenanceScreen") }
            )

            HousekeepingNavItem(
                title = "Envanter & Stok",
                icon = Icons.Default.ShoppingCart,
                isSelected = selectedTab == 2,
                onClick = { onNavigate("InventoryScreen") }
            )

            HousekeepingNavItem(
                title = "Vardiya Planı",
                icon = Icons.Default.Person,
                isSelected = selectedTab == 3,
                onClick = { onNavigate("ShiftPlannerScreen") }
            )
        }
    }
}

@Composable
fun HousekeepingNavItem(
    title: String,
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    val activeColor = Color(0xFF2563EB)
    val inactiveColor = Color(0xFF64748B)

    Surface(
        shape = RoundedCornerShape(12.dp),
        color = if (isSelected) Color(0xFFEFF6FF) else Color.Transparent,
        modifier = Modifier.clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = title,
                tint = if (isSelected) activeColor else inactiveColor,
                modifier = Modifier.size(18.dp)
            )
            Text(
                text = title,
                fontSize = 13.sp,
                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                color = if (isSelected) activeColor else inactiveColor
            )
        }
    }
}
